'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X } from 'lucide-react';
import { ChatPanel } from './ChatPanel';

// =============================================================================
// ChatWidget — floating bottom-right chat widget for the portal shell
// =============================================================================
//
// Mounted by the portal shell when shell.chatbot.enabled is true.
// Visual pattern follows the existing floating-popup pattern in the
// shell (e.g. DevAuthSwitcher), so the two widgets coexist naturally.
//
// Two states:
//
//   Closed: a circular button with a chat-bubble icon, bottom-right.
//   Open:   a panel anchored to the button (380x520 on desktop,
//           full-width gutter on mobile), with header + ChatPanel
//           body.
//
// When the bot/tenant isn't usable (412/403/404), the panel signals
// via onUnavailable and the widget hides itself entirely. We never
// show a broken UI to the visitor.
//
// Mount point (paste into the portal shell, next to the dev
// auth-switcher popup):
//
//   {shell.chatbot?.enabled && (
//     <ChatWidget
//       tenantSlug={tenantSlug}
//       chatbotId={shell.chatbot.chatbotId ?? null}
//       position={shell.chatbot.position ?? 'bottom-right'}
//       buttonLabel={shell.chatbot.buttonLabel ?? 'Need help?'}
//       openByDefault={shell.chatbot.openByDefault ?? false}
//     />
//   )}

export interface ChatWidgetProps {
  tenantSlug: string;
  chatbotId?: number | null;
  position?: 'bottom-right' | 'bottom-left';
  buttonLabel?: string;
  openByDefault?: boolean;
  greeting?: string;
}

export function ChatWidget({
  tenantSlug,
  chatbotId,
  position = 'bottom-right',
  buttonLabel,
  openByDefault = false,
  greeting,
}: ChatWidgetProps) {
  const { t } = useTranslation('site-builder-runtime');
  const label = buttonLabel ?? t('chat.buttonLabel');
  const [open, setOpen] = useState(openByDefault);
  const [hidden, setHidden] = useState(false);

  if (hidden) return null;

  const anchor = position === 'bottom-left' ? { bottom: 20, left: 20 } : { bottom: 20, right: 20 };

  return (
    <div
      style={{
        position: 'fixed',
        zIndex: 50,
        pointerEvents: 'none',
        ...anchor,
      }}
    >
      {open ? (
        <div
          className="shadow-2xl rounded-xl overflow-hidden"
          style={{
            pointerEvents: 'auto',
            width: 'min(380px, calc(100vw - 32px))',
            height: 'min(520px, calc(100vh - 100px))',
            background: 'var(--color-surface-card, #fff)',
            border: '1px solid var(--color-border-default, #e5e7eb)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Custom header with close button */}
          <div
            className="px-4 py-3 flex items-center justify-between"
            style={{
              background: 'var(--color-brand-500, #6366f1)',
              color: 'var(--color-brand-50, #fff)',
            }}
          >
            <div className="flex items-center gap-2 text-sm font-semibold">
              <MessageCircle className="h-4 w-4" />
              <span>{label}</span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1 rounded hover:bg-white/10"
              aria-label={t('chat.close')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Panel body — ChatPanel handles its own border, so we give
              it room to render without its own header. */}
          <div className="flex-1 min-h-0">
            <ChatPanel
              tenantSlug={tenantSlug}
              chatbotId={chatbotId}
              greeting={greeting}
              layout="floating"
              onUnavailable={() => setHidden(true)}
            />
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-full shadow-lg transition-transform hover:scale-105 flex items-center justify-center"
          style={{
            pointerEvents: 'auto',
            width: 56,
            height: 56,
            background: 'var(--color-brand-500, #6366f1)',
            color: 'var(--color-brand-50, #fff)',
          }}
          aria-label={label}
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}
